import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserPlus, UserMinus, BellRing, Video, MapPin, Phone, ShieldCheck,
  ShieldAlert, AlertTriangle, Activity, HeartPulse, ChevronRight, Search,
  Filter, RefreshCw, ClipboardList, Pill, Home as HomeIcon, Info,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  CartesianGrid,
} from "recharts";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { RISK_COLORS, SOURCE_COLORS } from "@/pages/command/constants";
import { isUnavailable, pct, moduleLabel } from "@/pages/command/unavailable";
import {
  StatTile, RiskTile, DonutMini, UtilCard, Unavailable, ChartUnavailable,
  SourceBanner, Figure, timeAgo, Loading,
} from "@/pages/command/components";
import "@/pages/command/command.css";

/*
 * Command Center — the seven leadership surfaces (R-016).
 *
 * ── Two contracts this file depends on ────────────────────────────────────
 *
 * 1. **Envelope.** Every endpoint wraps its payload in `{ data: … }`, and the
 *    patient explorer paginates as `{ data: [...], meta: { total } }`. The
 *    prototype's `{ items, total }` shape does not exist anywhere in the
 *    Laravel API (backendv2/docs/CLAUDE.md → Conventions); reading it is what
 *    R-011 / R-013 / R-015 each had to unpick.
 *
 * 2. **Source status.** A metric whose source module is not ported comes back
 *    as `null`, and the payload's `sources` block names what is missing. Null
 *    renders as "Not yet available" and never as a number, a bar width, or a
 *    chart segment — see ./unavailable.js. On a clinical safety dashboard the
 *    difference between "0 active alerts" and "alerting is not available" is
 *    the difference between a calm ward and an unmonitored one.
 *
 * Anything the API cannot compute is absent from this file. The prototype's
 * hard-coded utilisation percentages, its "vs yesterday" trend captions, and
 * its "Key Insights" panel — which asserted a 25% rise in high-risk patients
 * and a 1.2% readmission improvement from no data at all — are removed rather
 * than re-pointed at a live field, because the period-over-period comparisons
 * they claimed need historical snapshots no ported module stores.
 */

/** Unwraps `{ data: … }`. Every Command Center endpoint uses it. */
function unwrap(res) {
  return res?.data?.data ?? null;
}

/**
 * Loads one or more surfaces and routes failures to a single toast.
 *
 * A 422 from an unavailable filter is NOT a failure — it is the API answering
 * honestly — so callers that can trigger one handle that status themselves.
 */
function useSurfaces(load, deps) {
  const [state, setState] = useState({ data: null, loading: true });

  const run = useCallback(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    load()
      .then((data) => { if (!cancelled) setState({ data, loading: false }); })
      .catch((e) => {
        if (cancelled) return;
        setState({ data: null, loading: false });
        toast.error(formatApiError(e));
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => run(), [run]);

  return { ...state, reload: run };
}

// =====================================================
// EXECUTIVE DASHBOARD
// =====================================================
export function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [evaluating, setEvaluating] = useState(false);

  const { data, loading, reload } = useSurfaces(
    () => Promise.all([
      api.get("/command/executive"),
      api.get("/command/executive/feed"),
      api.get("/command/thresholds/breaches"),
    ]).then(([e, f, b]) => ({
      exec: unwrap(e),
      feed: unwrap(f),
      breaches: b.data?.data ?? [],
      breachMeta: b.data?.meta ?? {},
    })),
    [],
  );

  async function evaluate() {
    setEvaluating(true);
    try {
      const r = await api.post("/command/thresholds/evaluate");
      const { detected = [], skipped = [] } = r.data?.data ?? {};
      toast.success(
        `${detected.length} threshold breach${detected.length === 1 ? "" : "es"} detected`
        + (skipped.length ? ` · ${skipped.length} metric${skipped.length === 1 ? "" : "s"} could not be checked` : ""),
      );
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEvaluating(false);
    }
  }

  if (loading) return <Loading />;
  if (!data) return null;

  const { exec, feed, breaches, breachMeta } = data;
  const p = exec.patient, c = exec.clinical, o = exec.operational, w = exec.workforce;

  // Risk is the F37 surface: null until RPM persists it. No donut is drawn from
  // absent data — the cohort total comes from the patients table instead.
  const riskAvailable = c.risk_available === true;
  const riskData = riskAvailable
    ? [
      { name: "Stable", value: c.stable, color: "#10B981" },
      { name: "Moderate", value: c.moderate, color: "#F59E0B" },
      { name: "High", value: c.high, color: "#F97316" },
      { name: "Critical", value: c.critical, color: "#EF4444" },
    ]
    : null;

  const cohortTotal = p.total_active;
  const highRiskCount = riskAvailable ? (c.high ?? 0) + (c.critical ?? 0) : null;

  return (
    <div data-testid="cc-executive" className="cc-shell">
      <PageHeader
        title="Hospital @ Home Command Center"
        subtitle="Real-time operational and clinical intelligence"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={evaluate} disabled={evaluating} variant="outline" className="border-[#E5E7EB]" data-testid="cc-eval-thresholds">
              <ShieldAlert className="w-4 h-4 mr-1.5" /> {evaluating ? "Evaluating…" : "Evaluate Thresholds"}
            </Button>
            <Button onClick={reload} variant="outline" className="border-[#E5E7EB]">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      <SourceBanner sources={exec.sources} />

      {/* Only rendered when there is something real to alert on. A banner
          derived from unavailable risk data would be F37 with a red border. */}
      {(breaches.length > 0 || (highRiskCount ?? 0) > 0) && (
        <div className="cc-alert-banner" data-testid="cc-breach-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <strong className="text-[#991B1B]">
              {highRiskCount
                ? `${highRiskCount} high-risk patient${highRiskCount === 1 ? "" : "s"} require immediate review`
                : `${breaches.length} KPI threshold breach${breaches.length === 1 ? "" : "es"} open`}
            </strong>
          </div>
          {highRiskCount ? (
            <Button size="sm" variant="outline" onClick={() => navigate("/command/patient-explorer?risk=high")}
              className="border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2]">
              View Patients
            </Button>
          ) : null}
        </div>
      )}

      {/* Risk distribution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-1">
        <RiskTile icon={ShieldCheck} label="Stable" value={c.stable} pctVal={pct(c.stable, cohortTotal)} accent="#10B981" bg="#D1FAE5" module="rpm" />
        <RiskTile icon={ShieldAlert} label="Moderate Risk" value={c.moderate} pctVal={pct(c.moderate, cohortTotal)} accent="#F59E0B" bg="#FEF3C7" module="rpm" />
        <RiskTile icon={ShieldAlert} label="High Risk" value={c.high} pctVal={pct(c.high, cohortTotal)} accent="#F97316" bg="#FFEDD5" module="rpm" />
        <RiskTile icon={ShieldAlert} label="Critical" value={c.critical} pctVal={pct(c.critical, cohortTotal)} accent="#EF4444" bg="#FEE2E2" module="rpm" />
      </div>

      {/* The caption states what the cohort actually counts — the API reports
          whether its definition is exact or the superset standing in for it. */}
      <div className="text-[11px] text-[#6B7280] mb-4 flex items-start gap-1.5">
        <Users className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          Cohort: <span className="font-semibold text-[#1F2937]">{cohortTotal}</span>{" "}
          {exec.cohort?.definition ?? "active H@H patients"}
          {exec.cohort?.exact === false && (exec.cohort.missing || []).length > 0 && (
            <span className="text-[#94A3B8]">
              {" "}— not yet narrowed by {(exec.cohort.missing || []).map(moduleLabel).join(" / ")} enrollment
            </span>
          )}
        </span>
      </div>

      {/* Operations strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Active Patients" value={p.total_active} caption="H@H patients currently enrolled" onClick={() => navigate("/command/patient-explorer")} testId="cc-kpi-active" />
        <StatTile icon={UserPlus} accent="#10B981" label="New Admissions" value={p.new_admissions} caption="Admitted in the last 24 hours" />
        <StatTile icon={BellRing} accent="#F59E0B" label="Active Alerts" value={c.active_alerts} module="rpm" caption="RPM alerts unacknowledged" />
        <StatTile icon={Video} accent="#0B7E8C" label="Teleconsults Today" value={o.teleconsults_today} caption="Scheduled or completed today" onClick={() => navigate("/teleconsult")} />
        <StatTile icon={MapPin} accent="#10B981" label="Home Visits Today" value={o.home_visits_today} module="fleet" />
        <StatTile icon={Phone} accent="#8B5CF6" label="Hotline Calls Today" value={o.hotline_calls_today} module="hotline" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Risk donut — or an explanation of why there isn't one */}
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Patient Risk Distribution</h3>
          {riskData ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={riskData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {riskData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {riskData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} /> {d.name}
                    </span>
                    <span className="text-[#1F2937]">
                      {d.value} <span className="text-[#6B7280]">({pct(d.value, cohortTotal)}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <ChartUnavailable module="rpm" what="Risk distribution" height={280} />
          )}
        </div>

        {/* Patients by facility — live */}
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Patients by Facility</h3>
          {(feed.top_facilities || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={(feed.top_facilities || []).slice(0, 6)} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={140} />
                <Tooltip />
                <Bar dataKey="count" fill="#0B7E8C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="cc-empty">No active patients in scope.</div>
          )}
        </div>

        {/* Active interventions */}
        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[#1F2937]">Active Interventions</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          <InterventionList items={feed.interventions} />
        </div>
      </div>

      {/* Top diagnoses + live feed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-2">Top Diagnoses</h3>
          {(feed.top_diagnoses || []).length > 0 ? (
            (feed.top_diagnoses || []).map((d, i) => (
              <div key={i} className="flex items-center gap-3 mb-2.5">
                <span className="text-sm text-[#1F2937] w-40 truncate">{d.diagnosis}</span>
                <div className="flex-1 bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#0B7E8C] to-[#14B8A6] rounded-full" style={{ width: `${d.pct ?? 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-[#1F2937] w-12 text-right">{d.pct ?? "—"}%</span>
              </div>
            ))
          ) : (
            <div className="cc-empty">No diagnoses recorded for patients in scope.</div>
          )}
        </div>

        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[#1F2937]">Live Activity Feed</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          <ActivityFeed items={feed.feed} showScopeNote />
        </div>
      </div>

      {/* Workforce */}
      <h3 className="cc-section-title">Workforce</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <UtilCard icon={Users} accent="#0B7E8C" bg="#EAF8FA" label="Physicians" count={w.active_physicians} />
        <UtilCard icon={Users} accent="#10B981" bg="#D1FAE5" label="Nurses" count={w.active_nurses} />
        <UtilCard icon={Users} accent="#8B5CF6" bg="#EDE9FE" label="Coordinators" count={w.active_coordinators} />
        <UtilCard icon={Users} accent="#F59E0B" bg="#FEF3C7" label="Home Care Teams" count={w.active_home_teams} />
      </div>
      {w.utilisation_available === false && (
        <p className="cc-footnote">
          Utilisation is not shown: it needs shift rosters and assigned workload from
          the {moduleLabel("fleet")} and {moduleLabel("care_teams")} modules.
        </p>
      )}

      {breachMeta.blocked_metrics?.length > 0 && (
        <p className="cc-footnote">
          <Info className="w-3 h-3 inline mr-1" />
          {breachMeta.blocked_metrics.length} of the configured KPI thresholds cannot be
          evaluated yet (needs {[...new Set(breachMeta.blocked_metrics.map((b) => moduleLabel(b.blocked_by)))].join(", ")}),
          so an empty breach list does not mean every threshold is being checked.
        </p>
      )}
    </div>
  );
}

/** Shared intervention list — used by Executive and Clinical Safety. */
function InterventionList({ items }) {
  if (!items || items.length === 0) {
    return <div className="cc-empty">No active interventions.</div>;
  }

  return (
    <div>
      {items.map((i) => {
        const tone = i.severity === "critical" || i.severity === "high"
          ? { color: "#EF4444", bg: "#FEE2E2" }
          : i.severity === "medium" || i.severity === "urgent"
            ? { color: "#F59E0B", bg: "#FEF3C7" }
            : { color: "#10B981", bg: "#D1FAE5" };
        const Icon = i.source === "rpm" ? AlertTriangle
          : i.source === "order" ? Pill
            : i.source === "teleconsult" ? Video : ClipboardList;

        return (
          <div key={i.id} className="cc-intervention" data-testid={`cc-exec-intervention-${i.id}`}>
            <span className="cc-intervention-icon" style={{ background: tone.bg, color: tone.color }}>
              <Icon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="cc-intervention-title truncate capitalize">{i.title}</div>
              <div className="cc-intervention-sub">
                {i.patient_mrn ? `${i.patient_mrn} · ${i.patient_name}` : "Internal"}
              </div>
            </div>
            <div className="cc-intervention-meta">
              <div style={{ color: tone.color, fontWeight: 700 }} className="capitalize">{i.priority}</div>
              <div className="text-[10px]">{timeAgo(i.at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Shared activity feed. */
function ActivityFeed({ items, limit, showScopeNote }) {
  const rows = limit ? (items || []).slice(0, limit) : (items || []);

  if (rows.length === 0) return <div className="cc-empty">No recent activity.</div>;

  return (
    <>
      {rows.map((f, i) => {
        const tone = f.severity === "critical" || f.severity === "high"
          ? { color: "#EF4444", bg: "#FEE2E2" }
          : f.severity === "medium" || f.severity === "urgent"
            ? { color: "#F59E0B", bg: "#FEF3C7" }
            : { color: "#0B7E8C", bg: "#EAF8FA" };
        const Icon = f.type === "rpm_alert" ? AlertTriangle
          : f.type === "teleconsult_completed" ? Video
            : f.type === "patient_admitted" ? UserPlus
              : f.type === "home_visit_completed" ? HomeIcon : Activity;

        return (
          <div key={i} className="cc-feed-item">
            <div className="cc-feed-time">
              {new Date(f.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <span className="cc-feed-icon" style={{ background: tone.bg, color: tone.color }}>
              <Icon className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="cc-feed-title">{f.title}</div>
              <div className="cc-feed-sub">{f.subtitle}</div>
            </div>
          </div>
        );
      })}
      {showScopeNote && (
        <p className="cc-footnote">
          Covers teleconsultations and admissions. RPM alerts, hotline calls and home
          visits join this feed as those modules land.
        </p>
      )}
    </>
  );
}

// =====================================================
// OPERATIONAL DASHBOARD
// =====================================================
export function OperationalDashboard() {
  const navigate = useNavigate();

  const { data, loading } = useSurfaces(
    () => Promise.all([
      api.get("/command/operational"),
      api.get("/command/executive"),
    ]).then(([o, e]) => ({ ops: unwrap(o), exec: unwrap(e) })),
    [],
  );

  if (loading) return <Loading />;
  if (!data) return null;

  const { ops, exec } = data;
  const p = exec.patient;
  const tc = ops.teleconsultation;

  const totalTele = tc
    ? tc.scheduled + tc.awaiting_participants + tc.in_progress + tc.completed + tc.cancelled + tc.no_show
    : 0;

  return (
    <div data-testid="cc-operational" className="cc-shell">
      <PageHeader title="Operational Dashboard" subtitle="Workflow performance across modules" />

      <SourceBanner sources={ops.sources} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Active Patients" value={p.total_active} caption="H@H patients currently enrolled" />
        <StatTile icon={UserPlus} accent="#10B981" label="New Admissions" value={p.new_admissions} caption="Admitted in the last 24 hours" />
        <StatTile icon={UserPlus} accent="#8B5CF6" label="New Activations" value={p.new_activations} module="onboarding" />
        <StatTile icon={UserMinus} accent="#F59E0B" label="Discharges" value={p.discharges} caption="Discharged today" />
        <StatTile icon={UserMinus} accent="#EF4444" label="Rejected Referrals" value={p.rejected_referrals} module="onboarding" />
        <StatTile icon={Activity} accent="#8B5CF6" label="Avg Onboarding Time" value={p.avg_onboarding_days} module="onboarding" />
      </div>

      <div className="cc-chart-card mb-4">
        <h3 className="font-semibold text-[#1F2937] mb-3">Onboarding Pipeline</h3>
        <ChartUnavailable module="onboarding" what="The onboarding pipeline" height={160} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        {/* The one live funnel */}
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">
            Teleconsultations <span className="text-xs font-normal text-[#6B7280]">· Total: {totalTele}</span>
          </h3>
          {tc ? (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3">
                {[
                  ["Scheduled", tc.scheduled], ["Awaiting", tc.awaiting_participants],
                  ["In progress", tc.in_progress], ["Completed", tc.completed],
                  ["Cancelled", tc.cancelled], ["No show", tc.no_show],
                ].map(([label, v]) => (
                  <div key={label} className="text-center p-2 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                    <div className="text-base font-bold text-[#1F2937]">{v}</div>
                    <div className="text-[9px] uppercase tracking-wider text-[#6B7280]">{label}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <DonutMini value={pct(tc.completed, totalTele)} label="Completion Rate" />
                <DonutMini value={pct(tc.no_show, totalTele)} label="No Show Rate" tone="warn" />
                <div className="text-center">
                  <div className="cc-donut-label mb-1">Avg Consult Duration</div>
                  <div className="text-2xl font-bold text-[#1F2937]">
                    {isUnavailable(tc.avg_duration_min) ? <Unavailable compact /> : `${tc.avg_duration_min} min`}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <ChartUnavailable module="teleconsult" what="The teleconsult funnel" />
          )}
        </div>

        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Fleet / Home Visits</h3>
          <ChartUnavailable module="fleet" what="Home visit performance" height={240} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Hotline</h3>
          <ChartUnavailable module="hotline" what="Hotline activity" height={160} />
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Task Queues</h3>
          <ChartUnavailable module="orders" what="Task and MAR queues" height={160} />
        </div>
      </div>

      <h3 className="cc-section-title">SLA Performance</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Teleconsult SLA", "#3B82F6", Video, "teleconsult"],
          ["Onboarding SLA", "#0B7E8C", Users, "onboarding"],
          ["Fleet SLA", "#10B981", MapPin, "fleet"],
          ["Hotline SLA", "#F59E0B", Phone, "hotline"],
        ].map(([title, color, Icon, mod]) => (
          <button key={title} onClick={() => navigate(`/command/sla?module=${mod}`)} className="cc-card flex flex-col items-center text-center hover:shadow-md transition">
            <span className="w-12 h-12 rounded-full flex items-center justify-center mb-2" style={{ background: `${color}15`, color }}>
              <Icon className="w-6 h-6" />
            </span>
            <div className="font-semibold text-[#1F2937] mb-1">{title}</div>
            <div className="text-xs text-[#0B7E8C] font-semibold">View Details →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// =====================================================
// CLINICAL SAFETY
// =====================================================
export function ClinicalSafetyDashboard() {
  const navigate = useNavigate();

  const { data, loading } = useSurfaces(
    () => Promise.all([
      api.get("/command/clinical"),
      api.get("/command/executive/feed"),
      api.get("/command/interventions"),
    ]).then(([c, f, i]) => ({
      clinical: unwrap(c),
      feed: unwrap(f),
      interventions: unwrap(i),
    })),
    [],
  );

  if (loading) return <Loading />;
  if (!data) return null;

  const { clinical, feed, interventions } = data;
  const r = clinical.rpm_risk;
  const d = clinical.documentation;
  const k = interventions.kpis || {};

  return (
    <div data-testid="cc-clinical" className="cc-shell">
      <PageHeader title="Clinical Command Center" subtitle="Clinical overview and patient safety monitoring" />

      <SourceBanner sources={clinical.sources} />

      {/*
       * The most important honesty case in the app. If risk cannot be computed,
       * this page says so at the top rather than drawing a green donut — a
       * clinician glancing at this screen must not read "unmonitored" as
       * "stable". This panel is the UI half of F37's fix.
       */}
      {r.risk_available === false && (
        <div className="cc-warning-panel" data-testid="cc-risk-unavailable">
          <AlertTriangle className="w-5 h-5 shrink-0 text-[#B45309]" />
          <div>
            <strong>Patient risk scoring is not available.</strong> NEWS2 scores, risk
            levels and RPM alerts are computed by the {moduleLabel("rpm")} module, which
            is not part of this API yet. This dashboard cannot currently tell you
            whether any patient is deteriorating — use the RPM system directly.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Patient Risk Distribution</h3>
          <ChartUnavailable module="rpm" what="Risk distribution" height={230} />
          <div className="text-center mt-3">
            <div className="text-3xl font-bold text-[#1F2937]">{clinical.cohort_size}</div>
            <div className="text-xs text-[#6B7280]">Active patients in scope</div>
          </div>
        </div>

        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">High Risk Patients Queue</h3>
          <ChartUnavailable module="rpm" what="The high-risk queue" height={280} />
        </div>

        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-[#1F2937]">Latest Clinical Activity</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          <ActivityFeed items={feed.feed} limit={6} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-[#1F2937]">Active Interventions</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {[
              ["Open", k.open, "#8B5CF6", null],
              ["Escalated", k.escalated, "#EF4444", null],
              ["Overdue", k.overdue, "#F97316", "orders"],
              ["Done today", k.completed_today, "#10B981", null],
            ].map(([label, v, color, mod]) => (
              <div key={label} className="text-center p-2 rounded-lg" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                <div className="text-lg font-bold text-[#1F2937]"><Figure value={v} module={mod} /></div>
                <div className="text-[9px] uppercase tracking-wider" style={{ color }}>{label}</div>
              </div>
            ))}
          </div>
          <InterventionList items={(interventions.items || []).slice(0, 5)} />
        </div>

        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-2">Medication Compliance (MAR)</h3>
          <ChartUnavailable module="medications" what="MAR compliance" height={220} />
        </div>

        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Clinical Documentation</h3>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              ["Encounters", d.encounters, "#3B82F6", null],
              ["Physician", d.physician_notes, "#0B7E8C", "clinical_docs"],
              ["Nursing", d.nursing_notes, "#10B981", "clinical_docs"],
              ["Coordination", d.coordination_notes, "#F59E0B", "clinical_docs"],
            ].map(([label, v, color, mod]) => (
              <div key={label} className="text-center p-2 rounded-lg" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                <div className="text-base font-bold text-[#1F2937]"><Figure value={v} module={mod} /></div>
                <div className="text-[9px] uppercase tracking-wider" style={{ color }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// POPULATION HEALTH
// =====================================================
export function PopulationHealthDashboard() {
  const [facilityId, setFacilityId] = useState("all");
  const [facilities, setFacilities] = useState([]);

  const { data, loading } = useSurfaces(
    () => {
      const params = {};
      if (facilityId !== "all") params.facility_id = facilityId;
      return api.get("/command/population", { params }).then(unwrap);
    },
    [facilityId],
  );

  useEffect(() => {
    // per_page, not the prototype's page_size — Laravel drops unknown keys
    // silently rather than 422ing, so the wrong name caps the list at the
    // default page size with no visible error (R-015).
    api.get("/facilities", { params: { per_page: 50 } })
      .then((r) => setFacilities(r.data?.data ?? []))
      .catch(() => {});
  }, []);

  if (loading) return <Loading />;
  if (!data) return null;

  const female = data.by_gender?.find((g) => g.bucket === "Female")?.count ?? 0;

  return (
    <div data-testid="cc-population" className="cc-shell">
      <PageHeader
        title="Population Health Dashboard"
        subtitle="Cohort analysis and population insights"
        actions={
          <Select value={facilityId} onValueChange={setFacilityId}>
            <SelectTrigger className="w-56" data-testid="cc-pop-facility"><SelectValue placeholder="Facility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All facilities in scope</SelectItem>
              {facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      <SourceBanner sources={data.sources} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Total Active Patients" value={data.total} />
        <StatTile icon={ShieldAlert} accent="#F59E0B" label="High Risk Patients" value={null} module="rpm" />
        <StatTile icon={Activity} accent="#8B5CF6" label="Avg Age (Years)" value={data.avg_age_years} />
        <StatTile icon={Users} accent="#EC4899" label="Female Patients"
          value={data.total ? `${female} (${pct(female, data.total)}%)` : female} />
        <StatTile icon={HeartPulse} accent="#EF4444" label="Chronic Conditions" value={data.chronic_conditions} module="clinical_docs" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Risk Level</h4>
          <ChartUnavailable module="rpm" what="Risk cohorts" height={240} />
        </div>

        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Diagnosis (Top 5)</h4>
          {(data.by_diagnosis || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_diagnosis.slice(0, 5)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="bucket" type="category" tick={{ fontSize: 10 }} width={110} />
                <Tooltip />
                <Bar dataKey="count" fill="#0B7E8C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="cc-empty">No diagnoses recorded.</div>
          )}
          <p className="cc-footnote">
            Diagnosis is free text on the patient record, so these are exact-string
            groups. Coded disease burden needs the diagnoses lookup.
          </p>
        </div>

        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Age Group</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_age || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#14B8A6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Facility</h4>
          {(data.by_facility || []).length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#6B7280] text-[10px] uppercase">
                  <th className="text-left py-1">Facility</th><th className="text-right">#</th><th className="text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {data.by_facility.map((f) => (
                  <tr key={f.facility_id} className="border-t border-[#F1F5F9]">
                    <td className="py-1.5 text-[#1F2937]">{f.facility}</td>
                    <td className="text-right">{f.patients}</td>
                    <td className="text-right">{pct(f.patients, data.total)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="cc-empty">No patients in scope.</div>
          )}
        </div>

        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Gender</h4>
          {(data.by_gender || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.by_gender} dataKey="count" nameKey="bucket" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                  {data.by_gender.map((d, i) => <Cell key={i} fill={["#0B7E8C", "#EC4899", "#94A3B8"][i % 3]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="cc-empty">No patients in scope.</div>
          )}
        </div>

        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Nationality</h4>
          {(data.by_nationality || []).length > 0 ? (
            <table className="w-full text-xs">
              <tbody>
                {data.by_nationality.slice(0, 8).map((n) => (
                  <tr key={n.bucket} className="border-t border-[#F1F5F9]">
                    <td className="py-1.5 text-[#1F2937]">{n.bucket}</td>
                    <td className="text-right">{n.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="cc-empty">No nationality recorded.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================
// ACTIVE INTERVENTIONS
// =====================================================
export function InterventionsDashboard() {
  const navigate = useNavigate();
  const [source, setSource] = useState("all");
  const [priority, setPriority] = useState("all");

  const { data, loading } = useSurfaces(
    () => {
      const params = {};
      if (source !== "all") params.source = source;
      if (priority !== "all") params.priority = priority;
      return api.get("/command/interventions", { params }).then(unwrap);
    },
    [source, priority],
  );

  if (loading) return <Loading />;
  if (!data) return null;

  const k = data.kpis || {};
  const items = data.items || [];

  return (
    <div data-testid="cc-interventions" className="cc-shell">
      <PageHeader title="Active Interventions" subtitle="Live across the ported clinical modules" />

      <SourceBanner sources={data.sources} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile icon={ClipboardList} accent="#0B7E8C" label="Open" value={k.open} testId="cc-int-kpi-open" />
        <StatTile icon={AlertTriangle} accent="#EF4444" label="Escalated" value={k.escalated} />
        <StatTile icon={AlertTriangle} accent="#F97316" label="Overdue" value={k.overdue} module="orders" />
        <StatTile icon={ChevronRight} accent="#10B981" label="Completed Today" value={k.completed_today} />
      </div>

      <div className="cc-card mb-3 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#6B7280]" />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44" data-testid="cc-int-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="teleconsult">Teleconsult</SelectItem>
            <SelectItem value="rpm">RPM</SelectItem>
            <SelectItem value="hotline">Hotline</SelectItem>
            <SelectItem value="home_visit">Home Visit</SelectItem>
            <SelectItem value="order">Order</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="routine">Routine</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="cc-chart-card p-0 overflow-hidden">
        <table className="cc-table">
          <thead>
            <tr><th>Source</th><th>Type</th><th>Title</th><th>Patient</th><th>Facility</th><th>Priority</th><th>Status</th><th>Scheduled</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-[#6B7280]">No interventions match.</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} data-testid={`cc-int-row-${i.id}`}>
                <td>
                  <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                    style={{ background: `${SOURCE_COLORS[i.source]}15`, color: SOURCE_COLORS[i.source] }}>
                    {i.source}
                  </span>
                </td>
                <td className="capitalize">{(i.type || "").replace(/_/g, " ")}</td>
                <td className="font-medium text-[#1F2937] capitalize">{i.title}</td>
                <td>
                  {i.patient_id ? (
                    <button onClick={() => navigate(`/clinical/patients/${i.patient_id}`)} className="text-left text-[#0B7E8C] hover:underline">
                      {i.patient_name} <span className="font-mono text-xs text-[#94A3B8]">{i.patient_mrn}</span>
                    </button>
                  ) : <span className="text-[#94A3B8]">Internal</span>}
                </td>
                <td>{i.facility || "—"}</td>
                <td className="capitalize">{i.priority}</td>
                <td className="capitalize">{(i.status || "").replace(/_/g, " ")}</td>
                <td className="text-xs">{i.due_at ? new Date(i.due_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================
// SLA MANAGER
// =====================================================
export function SlaManager() {
  const [params, setParams] = useSearchParams();
  const [module, setModule] = useState(params.get("module") || "teleconsult");

  const { data, loading } = useSurfaces(
    () => api.get(`/command/sla/${module}`).then(unwrap),
    [module],
  );

  function pick(next) {
    setModule(next);
    setParams({ module: next }, { replace: true });
  }

  return (
    <div data-testid="cc-sla" className="cc-shell">
      <PageHeader
        title="SLA Manager"
        subtitle="Workflow funnels, bottlenecks and SLA visibility"
        actions={
          <Select value={module} onValueChange={pick}>
            <SelectTrigger className="w-52" data-testid="cc-sla-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="teleconsult">Teleconsult</SelectItem>
              <SelectItem value="onboarding">Onboarding</SelectItem>
              <SelectItem value="rpm">RPM</SelectItem>
              <SelectItem value="hotline">Hotline</SelectItem>
              <SelectItem value="fleet">Fleet / Home Visits</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {loading ? <Loading /> : !data ? null : !data.available ? (
        <div className="cc-chart-card">
          <ChartUnavailable module={module} what={`The ${module} funnel`} height={220} />
        </div>
      ) : (
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-4 capitalize">{module} Funnel</h3>
          {(() => {
            const max = Math.max(...data.stages.map((s) => s.count || 0), 1);
            return data.stages.map((s, i) => (
              <div key={i} className="cc-funnel-row">
                <span className="cc-funnel-label">{s.stage}</span>
                <div className="cc-funnel-bar">
                  <div className="cc-funnel-fill" style={{ width: `${Math.max(8, (s.count / max) * 100)}%` }} />
                  <div className="cc-funnel-text">
                    {s.count}{s.conversion_pct != null ? ` · ${s.conversion_pct}%` : ""}
                  </div>
                </div>
              </div>
            ));
          })()}

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile icon={AlertTriangle} accent="#F59E0B" label="No-Show" value={data.no_show} />
            <StatTile icon={ClipboardList} accent="#94A3B8" label="Cancelled" value={data.cancelled} />
            <StatTile icon={Activity} accent="#0B7E8C" label="Avg Duration (min)" value={data.avg_duration_min} />
            <StatTile icon={ChevronRight} accent="#10B981" label="Completion %"
              value={isUnavailable(data.completion_rate_pct) ? null : `${data.completion_rate_pct}%`} />
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// PATIENT EXPLORER
// =====================================================
export function PatientExplorer() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [facilities, setFacilities] = useState([]);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    facility_id: "all",
    risk: params.get("risk") || "all",
    news2_bucket: "all",
    gender: "all",
    age_bucket: "all",
    diagnosis: "",
  });

  /*
   * A filter the API cannot honour comes back 422 with the blocking module
   * named. That is not an error to toast away — it is the answer, and it is
   * rendered inline where the results would have been. The alternative
   * (ignoring the filter and listing everyone) would answer "show me the
   * high-risk patients" with every active patient in the cohort.
   */
  const [blocked, setBlocked] = useState(null);

  const { data, loading } = useSurfaces(
    () => {
      const p = { page, per_page: 25 };
      if (q.trim()) p.q = q.trim();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") p[k] = v;
      });

      return api.get("/command/patient-explorer", { params: p })
        .then((r) => {
          setBlocked(null);
          return { items: r.data?.data ?? [], total: r.data?.meta?.total ?? 0 };
        })
        .catch((e) => {
          if (e?.response?.status === 422 && e.response.data?.unavailable_source) {
            setBlocked({
              module: e.response.data.unavailable_source,
              fields: Object.keys(e.response.data.errors || {}),
            });
            return { items: [], total: 0 };
          }
          throw e;
        });
    },
    [q, filters, page],
  );

  useEffect(() => {
    api.get("/facilities", { params: { per_page: 50 } })
      .then((r) => setFacilities(r.data?.data ?? []))
      .catch(() => {});
  }, []);

  function setFilter(key, value) {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function clearRiskFilters() {
    setPage(1);
    setFilters((f) => ({ ...f, risk: "all", news2_bucket: "all" }));
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div data-testid="cc-explorer" className="cc-shell">
      <PageHeader
        title="Patient Explorer"
        subtitle={blocked ? "Filter not available" : `${total} matching active patient${total === 1 ? "" : "s"}`}
      />

      <div className="cc-card mb-3 grid grid-cols-2 md:grid-cols-7 gap-2">
        <div className="relative col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Name, MRN…" className="pl-9" data-testid="cc-explorer-q" />
        </div>

        <Select value={filters.facility_id} onValueChange={(v) => setFilter("facility_id", v)}>
          <SelectTrigger data-testid="cc-explorer-facility"><SelectValue placeholder="Facility" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All facilities</SelectItem>
            {facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filters.risk} onValueChange={(v) => setFilter("risk", v)}>
          <SelectTrigger data-testid="cc-explorer-risk"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All risk</SelectItem>
            <SelectItem value="stable">Stable</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.news2_bucket} onValueChange={(v) => setFilter("news2_bucket", v)}>
          <SelectTrigger><SelectValue placeholder="NEWS2" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All NEWS2</SelectItem>
            <SelectItem value="0-2">0-2</SelectItem>
            <SelectItem value="3-4">3-4</SelectItem>
            <SelectItem value="5-6">5-6</SelectItem>
            <SelectItem value="7+">7+</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.age_bucket} onValueChange={(v) => setFilter("age_bucket", v)}>
          <SelectTrigger><SelectValue placeholder="Age" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            <SelectItem value="<18">{"<18"}</SelectItem>
            <SelectItem value="18-39">18-39</SelectItem>
            <SelectItem value="40-59">40-59</SelectItem>
            <SelectItem value="60-74">60-74</SelectItem>
            <SelectItem value="75+">75+</SelectItem>
          </SelectContent>
        </Select>

        <Input value={filters.diagnosis} onChange={(e) => setFilter("diagnosis", e.target.value)} placeholder="Diagnosis…" />
      </div>

      {blocked ? (
        <div className="cc-warning-panel" data-testid="cc-explorer-blocked">
          <AlertTriangle className="w-5 h-5 shrink-0 text-[#B45309]" />
          <div>
            <strong>
              Cannot filter by {blocked.fields.map((f) => f.replace(/_/g, " ")).join(" or ")} yet.
            </strong>{" "}
            These are computed by the {moduleLabel(blocked.module)} module, which is not
            part of this API yet. No patient list is shown, because listing the
            unfiltered cohort here would present every active patient as matching
            your filter.
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={clearRiskFilters}>
                Clear these filters
              </Button>
            </div>
          </div>
        </div>
      ) : loading ? (
        <Loading />
      ) : (
        <>
          <div className="cc-chart-card p-0 overflow-hidden">
            <table className="cc-table">
              <thead>
                <tr>
                  <th>Name</th><th>MRN</th><th>Facility</th><th>Diagnosis</th>
                  <th>Risk</th><th>NEWS2</th><th>Care Team</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-[#6B7280]">No patients match your filters.</td></tr>
                )}
                {items.map((p) => (
                  <tr key={p.id} data-testid={`cc-explorer-row-${p.id}`}>
                    <td className="font-medium text-[#1F2937]">
                      {p.name} <span className="text-[10px] text-[#94A3B8]">{p.age}y · {p.gender}</span>
                    </td>
                    <td className="font-mono text-xs text-[#6B7280]">{p.mrn}</td>
                    <td className="text-xs text-[#6B7280]">{p.facility}</td>
                    <td className="text-xs">{p.diagnosis || "—"}</td>
                    <td>
                      {isUnavailable(p.risk_level) ? <Unavailable module="rpm" compact /> : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                          style={{ background: `${RISK_COLORS[p.risk_level]}15`, color: RISK_COLORS[p.risk_level] }}>
                          {p.risk_level}
                        </span>
                      )}
                    </td>
                    <td><Figure value={p.news2_score} module="rpm" /></td>
                    <td className="text-xs">
                      {isUnavailable(p.physician) && isUnavailable(p.nurse)
                        ? <Unavailable module="care_teams" compact />
                        : `${p.physician || "—"} / ${p.nurse || "—"}`}
                    </td>
                    <td className="text-right">
                      <Button size="sm" onClick={() => navigate(`/clinical/patients/${p.id}`)}
                        className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                        data-testid={`cc-explorer-open-${p.id}`}>
                        Open <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 25 && (
            <div className="flex items-center justify-between mt-3 text-sm text-[#6B7280]">
              <span>Showing {items.length} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={page * 25 >= total} onClick={() => setPage((n) => n + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
